import { observer } from "mobx-react-lite"
import { useMemo } from "react"
import Constants from "../../../api/Constants"
import { useNewsItemFillHeight } from "../../../hooks/useCalcHeight"
import useNow, { UpdateFrequency } from "../../../hooks/useNow"
import useUpdateInterval from "../../../hooks/useUpdateInterval"
import { useRootStore } from "../../../stores/RootStoreContext"
import OverflowPager from "../../overflow-pager/OverflowPager"
import { isCustomSectionEnabled } from "../custom-sections/customSections"
import WochenbriefKZO from "../custom-sections/WochenbriefKZO"
import "./News.scss"
import NewsEmptyState from "./NewsEmptyState"
import { isVisible } from "./NewsRestrictions"
import NewsElement from "./elements/NewsElement"

interface NewsProps {
  fullWidthLayout?: boolean
}

const NEWS_PAGING_DURATION_IN_SECONDS = 10
const DISTANCE_BETWEEN_NEWS_ELEMENTS = 20

const News = (props: NewsProps) => {
  const { newsStore, mensaStore, configurationStore, wochenbriefStore } = useRootStore()
  const time = useNow(UpdateFrequency.Minute)

  const height =
    useNewsItemFillHeight(mensaStore.isMensaMenueVisible) + DISTANCE_BETWEEN_NEWS_ELEMENTS

  const isWochenbriefEnabled = isCustomSectionEnabled(
    "kzo-wochenbrief",
    configurationStore.display,
  )
  const letter = isWochenbriefEnabled ? wochenbriefStore.letter : null
  const showWochenbrief = !!letter?.content

  useUpdateInterval(
    () => newsStore.load(),
    [newsStore.load],
    Constants.UpdateIntervals.UPDATE_NEWS_ALL_SECONDS * 1000,
  )

  useUpdateInterval(
    () => {
      if (isWochenbriefEnabled) wochenbriefStore.load()
    },
    [wochenbriefStore.load, isWochenbriefEnabled],
    Constants.UpdateIntervals.UPDATE_WOCHENBRIEF_ALL_SECONDS * 1000,
    isWochenbriefEnabled,
  )

  const visibleNews = useMemo(
    () =>
      newsStore.news.filter((news) => {
        const isVisibleOnAnyDisplay = news.displayMode !== "targeted"
        const isVisibleOnThisDisplay =
          news.displayMode === "targeted" &&
          configurationStore.display?.identifier &&
          news.limitedToDisplays?.includes(configurationStore.display?.identifier)

        return (
          news.isVisible &&
          (isVisibleOnAnyDisplay || isVisibleOnThisDisplay) &&
          isVisible(news, time)
        )
      }),
    [newsStore.news, configurationStore.display?.identifier, time],
  )
  const newsElements = visibleNews
    .filter((news) => news.type !== "callout")
    .map((news) => <NewsElement key={news.id} news={news} />)
  const newsCallouts = visibleNews
    .filter((news) => news.type === "callout")
    .map((news) => <NewsElement key={news.id} news={news} />)

  const newsLeftColumn = newsElements.filter((_, index) => index % 2 === 0)
  const newsRightColumn = newsElements.filter((_, index) => index % 2 === 1)

  const showColumns = newsElements.length > 1 && !props.fullWidthLayout

  if (visibleNews.length === 0 && !showWochenbrief) {
    return props.fullWidthLayout ? <NewsEmptyState /> : null
  }

  if (visibleNews.length === 0 && letter) {
    return (
      <div className="news-area wochenbrief-only">
        <WochenbriefKZO letter={letter} />
      </div>
    )
  }

  return (
    <div className="news-area" style={{ height: `${height / 10}rem` }}>
      <OverflowPager pagingDurationInSeconds={NEWS_PAGING_DURATION_IN_SECONDS}>
        {(newsElements.length > 0 || newsCallouts.length > 0) && (
          <div className="news-page">
            {newsCallouts.length > 0 && <div className="news-callouts">{newsCallouts}</div>}
            {newsElements.length > 0 &&
              (showColumns ? (
                <div className="news-columns">
                  <div className="news-column column-left">{newsLeftColumn}</div>
                  <div className="news-column column-right">{newsRightColumn}</div>
                </div>
              ) : (
                <div className="news-wide">{newsElements}</div>
              ))}
          </div>
        )}
        {showWochenbrief && letter && <WochenbriefKZO letter={letter} />}
      </OverflowPager>
    </div>
  )
}

export default observer(News)
